import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocalStorage } from '@rehooks/local-storage';
import { VASTClient, VASTTracker } from '@dailymotion/vast-client'

export type MediaQuality = 'hd' | 'sd';

export interface VideoPlayerContextType {
    vastTracker: any;
    triggerAd: () => void;
    adsPlayed: number;
    setAdsPlayed: (adsPlayed: number) => void;
    adActive: boolean;
    setAdActive: (playing: boolean) => void;
    loaded: boolean;
    setLoaded: (loaded: boolean) => void;
    isError: boolean;
    setIsError: (isError: boolean) => void;
    playing: boolean;
    setPlaying: (playing: boolean) => void;
    muted: boolean;
    setMuted: (muted: boolean) => void;
    quality: MediaQuality,
    setQuality: (quality: MediaQuality) => void;
    videoRef: any;
    progress: number;
    setProgress: (progress: number) => void;
    hidden: boolean;
    setHidden: (hidden: boolean) => void;
    showControls: (autoHideTimeout?: number) => void;
    autoPlayInView: boolean;
}
export const VideoPlayerContext = React.createContext<VideoPlayerContextType>({
    vastTracker: undefined,
    triggerAd: () => {},
    adsPlayed: 0,
    setAdsPlayed: () => {},
    adActive: false,
    setAdActive: () => {},
    isError: false,
    setIsError: () => {},
    loaded: false,
    setLoaded: () => {},
    playing: false,
    setPlaying:  () => {},
    muted: false,
    setMuted: () => {},
    videoRef: null,
    quality: 'sd',
    setQuality: () => {},
    progress: 0,
    setProgress: () => {},
    hidden: false,
    setHidden: () => {},
    showControls: (autoHideTimeout?: number) => {},
    autoPlayInView: false,
});

export interface VideoPlayerContextProviderProps {
    autoPlayInView?: boolean;
    children: any;
}
export const VideoPlayerContextProvider = ({ children, autoPlayInView = true }: VideoPlayerContextProviderProps) => {
    const [ loaded, setLoaded ] = useState(false);
    const [ isError, setIsError ] = useState(false);
    const [ playing, setPlaying ] = useState(false);
    const [ muted, setMuted ] = useState(true);
    const [ quality, setQuality ] = useLocalStorage<MediaQuality>('quality', 'hd');
    const [ progress, setProgress ] = useState(0);
    const [hidden, setHidden] = useState(true);
    const [ timer, setTimer ] = useState<any>();
    const vastClient = useRef<any>(new VASTClient());
    const [ vastTracker, setVastTracker] = useState<any>();
    const loadingAd = useRef<boolean>(false);
    const [ adActive, setAdActive ] = useState<boolean>(false);
    const [ adsPlayed, setAdsPlayed ] = useState<number>(0);
    const resumeTime = useRef<number | undefined>();

    const videoRef = useRef<HTMLVideoElement>();

    const loadAd = async () => {
        if (loadingAd.current || adActive) {
            return;
        }

        loadingAd.current = true;
        
        try {
            const resp = await vastClient.current.get('https://syndication.realsrv.com/splash.php?idzone=4309354');
            const { ads } = resp;
    
            if (ads.length == 1) {
                const ad = ads[0];
                const creative = ads[0].creatives[0];
    
                const vastTracker = new VASTTracker(vastClient.current, ad, creative);
                vastTracker.on('clickthrough', (url: string) => {
                    window.open(url, '_blank')
                });
                setVastTracker(vastTracker);

                resumeTime.current = videoRef.current?.currentTime || 0;
    
                setAdsPlayed((adsPlayed) => adsPlayed + 1);
                setAdActive(true);
            }
        }
        finally {
            loadingAd.current = false;
        }
    }

    useEffect(() => {
        if (videoRef.current && loaded) {
            if (playing === true) {
                videoRef.current.play();
                if (!adActive && resumeTime.current) {
                    videoRef.current.currentTime = resumeTime.current;
                    resumeTime.current = undefined;
                }
                showControls();
            }
            else if (playing === false && !videoRef.current.paused) {
                videoRef.current.pause();
            }
        }
    }, [playing, videoRef, loaded, adActive]);

    useEffect(() => {
        if (adActive) {
            vastTracker.setPaused(!playing);
        }
    }, [adActive, playing, vastTracker])

    useEffect(() => {
        setLoaded(false);
    }, [quality]);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.muted = muted;
        }
    }, [muted, videoRef]);

    const showControls = (autoHideTimeout: number = 2000) => {
        setHidden(false);
        if (timer) {
            clearTimeout(timer);
        }

        const timeout = setTimeout(() => {
            if (playing) {
                setHidden(true);

            }
        }, autoHideTimeout);
        setTimer(timeout);
    }

    const triggerAd = () => {
        loadAd();
    }

    const value = {
        vastTracker,
        adsPlayed,
        setAdsPlayed,
        adActive,
        setAdActive,
        triggerAd,
        loaded,
        setLoaded,
        isError,
        setIsError,
        playing,
        setPlaying,
        muted,
        setMuted,
        quality,
        setQuality,
        videoRef,
        progress,
        setProgress,
        hidden,
        setHidden,
        showControls,
        autoPlayInView
    };

    return (
        <VideoPlayerContext.Provider value={value}>
            {children}
        </VideoPlayerContext.Provider>
    );
}